import { useState, useEffect } from 'react';
import { useSetAtom, useStore } from 'jotai';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Building2, User } from 'lucide-react';
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { googleAdsAccountsQueryOptions, useSetGoogleAdsCustomerIdMutationOptions } from '@/lib/query-options';
import type { GoogleAdsAccount, OrganizationDataWithMembership } from '@/lib/types';
import { useToast } from '@/hooks/useToast';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';

interface OrganizationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizations: OrganizationDataWithMembership[];
  selectedOrganization: OrganizationDataWithMembership | null;
  /** Performs the actual switch. May resolve `false` when the switch was aborted. */
  onOrganizationSelect: (organization: OrganizationDataWithMembership) => Promise<boolean | void>;
}

export function OrganizationSelectionModal({
  isOpen,
  onClose,
  organizations,
  selectedOrganization,
  onOrganizationSelect
}: OrganizationSelectionModalProps) {
  const [tempSelection, setTempSelection] = useState<string>(selectedOrganization?.slug || '');
  // #6452: previewing an org (or an account) in this modal must NOT touch the
  // live googleAdsAccountAtom — the preview lives in local state and is only
  // committed on Confirm. Cancel simply discards it, so nothing to restore.
  const [previewAccount, setPreviewAccount] = useState<GoogleAdsAccount | null>(null);
  const { data: googleAdsAccounts = [] } = useSuspenseQuery(googleAdsAccountsQueryOptions());
  const { toast } = useToast();
  const googleAdsCustomerMutation = useSetGoogleAdsCustomerIdMutationOptions();
  const queryClient = useQueryClient();
  const store = useStore();

  // Global state atoms — written only on Confirm.
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const setOrganizationData = useSetAtom(organizationDataAtom);

  // Campaign counts are now included in the organizations data
  const campaignCounts: Record<number, number> = {};
  organizations.forEach(org => {
    if (org.campaign_count !== undefined) {
      campaignCounts[org.id] = org.campaign_count;
    }
  });

  // Update tempSelection when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelection(selectedOrganization?.slug || '');
    }
  }, [isOpen, selectedOrganization]);

  // Derive the PREVIEW Google Ads account whenever the previewed org changes.
  useEffect(() => {
    if (isOpen) {
      const selectedOrg = organizations.find((org) => org.slug === tempSelection);
      const currentAccountId = selectedOrg?.membership?.last_selected_googleads_account_id;

      if (currentAccountId) {
        const account = googleAdsAccounts.find(acc => acc.googleads_account_id === currentAccountId);
        setPreviewAccount(account ?? null);
      } else {
        setPreviewAccount(null);
      }
    }
  }, [isOpen, tempSelection, organizations, googleAdsAccounts]);


  const handleConfirm = async () => {
    const selectedOrg = organizations.find((org) => org.slug === tempSelection);
    if (selectedOrg) {
      // First switch organization — this persists the selection to the backend
      // and purges org-scoped state. It resolves false (or leaves the org atom
      // unchanged) when the backend refused the switch; the preview is then
      // discarded and nothing is committed (#6449/#6452).
      const result = await onOrganizationSelect(selectedOrg);
      const committedOrg = store.get(organizationDataAtom);
      if (result === false || committedOrg?.id !== selectedOrg.id) {
        onClose();
        return;
      }

      // Commit the previewed Google Ads account now that the switch landed.
      if (previewAccount) {
        setGoogleAdsAccount(previewAccount);
        const updatedOrganization = {
          ...selectedOrg,
          membership: {
            ...selectedOrg.membership,
            last_selected_googleads_account_name: previewAccount.name || previewAccount.googleads_account_id,
            last_selected_googleads_account_id: previewAccount.googleads_account_id,
          }
        };
        setOrganizationData(updatedOrganization);

        // Persist the account selection when it differs from the org's saved one.
        if (previewAccount.googleads_account_id !== selectedOrg.membership?.last_selected_googleads_account_id) {
          try {
            await googleAdsCustomerMutation.mutateAsync({
              googleAdsAccountId: previewAccount.googleads_account_id,
              organizationId: selectedOrg.id
            });
            // Invalidate conversion actions query to refresh data for the new account
            queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
          } catch (error) {
            console.error('Failed to update Google Ads account:', error);
            toast({
              title: 'Error',
              description: 'Failed to update Google Ads account',
              variant: 'destructive',
            });
          }
        }
      } else {
        // Clear Google Ads account selection for organizations with no connected account
        setGoogleAdsAccount(null);
        const updatedOrganization = {
          ...selectedOrg,
          membership: {
            ...selectedOrg.membership,
            last_selected_googleads_account_name: null,
            last_selected_googleads_account_id: null,
          }
        };
        setOrganizationData(updatedOrganization);
      }
    }
    onClose();
  };

  const handleCancel = () => {
    // Discard the preview — live atoms were never touched (#6452).
    setTempSelection(selectedOrganization?.slug || '');
    setPreviewAccount(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Switch Organization</DialogTitle>
          <DialogDescription>
            Choose the organization you want to work with from the list below.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">Organization</label>
            <Select value={tempSelection} onValueChange={setTempSelection}>
              <SelectTrigger className="w-full">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => {
                  const campaignCount = campaignCounts[org.id] ?? 0;
                  return (
                    <SelectItem key={org.slug} value={org.slug} className="cursor-pointer">
                      {org.name || org.slug} ({campaignCount} campaign{campaignCount !== 1 ? 's' : ''})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {googleAdsAccounts.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">Google Ads Account</label>
              <Select
                value={previewAccount?.googleads_account_id || 'none'}
                onValueChange={(value) => {
                  // Preview only — committed (atom + backend) on Confirm (#6452).
                  if (value === 'none') {
                    setPreviewAccount(null);
                  } else {
                    const account = googleAdsAccounts.find(acc => acc.googleads_account_id === value);
                    setPreviewAccount(account ?? null);
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select a Google Ads account" />
                </SelectTrigger>
                <SelectContent>
                  {!previewAccount && (
                    <SelectItem value="none" className="cursor-pointer">None</SelectItem>
                  )}
                  {googleAdsAccounts.map((account) => (
                    <SelectItem key={account.googleads_account_id} value={account.googleads_account_id} className="cursor-pointer">
                      {account.name || account.googleads_account_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!tempSelection}>
            Select
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
